import type {ComponentType} from 'react';

import ReactDOMServer from 'react-dom/server';

/**
 * The `iconAsBackground` function is designed to convert a React component representing an icon into a data URI string that can be used as a background image in CSS.
 * It takes a `ComponentType` representing the icon and an optional props object to be spread into the icon component.
 * This function is crucial for dynamically generating background images in styles, allowing for greater flexibility and customization in styling components.
 *
 * @param Icon  A `ComponentType` representing the React component of the icon to be converted.
 * @param props An optional object representing the props to be spread into the icon component.
 * @returns      A string representing the data URI of the rendered icon, which can be used as a background image in CSS.
 *
 * @example
 * ```tsx
 * const backgroundImage = iconAsBackground(MyIcon, { color: 'red' });
 * ```
 */
export const iconAsBackground = (Icon: ComponentType, props: object): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    // eslint-disable-next-line react/jsx-props-no-spreading
    ReactDOMServer.renderToStaticMarkup(<Icon {...props} />)
)}`;

/**
 * Inserts line breaks into a given text string by replacing newline characters (`\n`) with `<br />` elements.
 *
 * This function is useful for rendering multi-line strings within JSX where newline characters should be
 * translated into actual HTML line breaks. It splits the input string by newline characters and interleaves
 * them with `<br />` elements, except after the final line. If the input is empty or falsy, it returns an empty string.
 *
 * This is especially helpful for preserving formatting in user-generated content or CMS-managed strings
 * without manual HTML editing.
 *
 * @param text The input text string that may contain newline characters (`\n`).
 * @returns An array of strings and `<br />` elements for rendering in JSX, or an empty string if input is falsy.
 *
 * @example
 * ```tsx
 * <p>{setLineBreaks("Line one\nLine two\nLine three")}</p>
 * // Renders:
 * // Line one
 * // <br />
 * // Line two
 * // <br />
 * // Line three
 * ```
 */
export const setLineBreaks = (text: string) => {
    if (!text) {
        return [''];
    }

    return text.split('\n').flatMap((line, index, arr) => {
        if (index < arr.length - 1) {
            // eslint-disable-next-line react/jsx-key
            return [line, <br />];
        }

        return [line];
    });
};